import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DefaultStyles from '../../../constants/DefaultFormStyles';
import FieldModes from '../../../constants/FieldModes';
import FieldTypes from '../../../constants/FieldTypes';
import { isEmptyValue } from '../../../lib/validation';

const StyledWrapper = styled.div`
  height: ${props => props.measure ? 'auto' : '100%'};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const StyledBlock = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${(props) => props.backgroundColor || DefaultStyles.backgroundColor};
  font-size:${(props) => (props.fontSize !== undefined ? `${props.fontSize}px` : DefaultStyles.fontSize)} !important;
  color: ${(props) => props.fontColor || DefaultStyles.fontColor};
  font-style: ${(props) => props.fontStyle || 'normal'};
  font-weight: ${(props) => props.fontWeight || 'normal'};
  text-align: ${(props) => props.textAlign || 'left'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  ${(props) => (props.textOverflow ? `text-overflow: ${props.textOverflow};` : '')}
  overflow: ${(props) => (props.textOverflow ? 'hidden;' : 'visible;')}
  border-radius: ${(props) => (props.borderRadius !== undefined ? `${props.borderRadius}px` : DefaultStyles.borderRadius)};
  border: ${(props) => props.border};
  padding: ${(props) => (props.padding !== undefined ? `${props.padding}px` : DefaultStyles.padding)};
  resize: none;
`;

const StyledTextarea = styled.textarea`
  border: none;
  width: 100%;
  height: 100%;
  resize: none;
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-align: inherit;
  text-transform: inherit;
  text-decoration: inherit;
  background-color: transparent;
  &:focus {
    outline: none;
  }

  &:active {
    outline: none;
  }
`;

const StyledDisplayText = styled.div`
  white-space: pre-wrap; /* Preserve line breaks */
  width: 100%;
  height: 100%;
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-align: inherit;
  text-transform: inherit;
  text-decoration: inherit;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class BlockField extends React.PureComponent {

  static propTypes = {

    fieldPositionId: PropTypes.string,

    value: PropTypes.string,
    disabled: PropTypes.bool,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

  }

  static defaultProps = {
    value: ''
  }

  constructor(props) {
    super(props);
    this.state = {
      isEditing: false,
    };
  }

  componentDidUpdate(prevProps) {

    const { wrapperRef } = this.state;
    const { value, measure, onMeasureCompleted, measuredHeight, measuredOn, updatedOn, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (prevProps.value !== value) {
      requestAnimationFrame(() => {
        this.setState({ isEditingHeight: wrapperRef.getBoundingClientRect().height });
      });
    }

    // Block fields should only be measured if they have a populated value
    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.block);
    //Important Note: Vanilla JS initally has a height of 0. We need to ensure there is a height before we can measure. This is why we check for the bounding client rect.
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    if (wrapperRef && measure && onMeasureCompleted && hasPopulatedValue && refHeightAvailable && (!measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn))) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, { height: refHeightAvailable, measuredOn: new Date().getTime() });
        this.setState({ isEditingHeight: refHeightAvailable });
      });
    }

  }

  render() {

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      disabled,
      fieldMode,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,
      measure,
      value
    } = this.props;

    const { isEditing, isEditingHeight, wrapperRef } = this.state;

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const bWidth = borderWidth !== undefined ? `${borderWidth}px` : DefaultStyles.borderWidth;
    const bColor = borderColor || 'transparent';
    const border = `${bWidth} solid ${bColor}`;
    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.block);

    return (
      <StyledWrapper 
        {...{ margin, measure: measure && hasPopulatedValue }}
        ref={(e) => {
          if (e && !wrapperRef && !isEditingHeight) this.setState({ wrapperRef: e, isEditingHeight: e.getBoundingClientRect().height });
        }}
      >
        <StyledBlock
          data-testid={`${fieldPositionId}-block-field`}
          border={border}
          {...{
            disabled,

            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
          }}
          onClick={this.handleEdit}
        >
          {!isEditing || fieldMode === FieldModes.readonly ? (
            <StyledDisplayText>{value}</StyledDisplayText>
          ) : (
            <StyledTextarea
              defaultValue={value}
              rows={2}
              style={{ height: isEditingHeight ? `${isEditingHeight}px` : 'auto' }}
              onBlur={this.handleBlur}
              readOnly={fieldMode !== FieldModes.edit}
              autoFocus
            />
          )}
        </StyledBlock>
      </StyledWrapper>
    );
  }

  handleEdit = () => {
    this.setState({ isEditing: true });
  }

  handleBlur = (e) => {

    const { onChange, fieldId, fieldIdentifier, fieldPositionId, disabled } = this.props;
    
    if (onChange && !disabled) {
      onChange(fieldId, fieldIdentifier, fieldPositionId, e.target.value);
      this.setState({ isEditing: false });
    }
  }

}

export default BlockField;
